import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Botao, Campo } from '../componentes/ui';
import { listarEmpresas, type EmpresaResumo } from '../servicos/empresas';
import { formatarCnpj } from '../servicos/formato';

/** Quantas empresas a busca mostra de uma vez: o bastante para achar, pouco para rolar. */
const RESULTADOS = 8;

/**
 * Escolher uma empresa pela busca, não por lista suspensa.
 *
 * São centenas de empresas e a listagem para em 100 por página: uma lista
 * suspensa esconderia parte delas sem avisar. A busca vai ao servidor — por
 * razão social ou nome fantasia, como na tela de Empresas — e só depois de uma
 * pausa na digitação, para não disparar uma requisição por tecla.
 *
 * Empresa inativa aparece, mas não se escolhe: sumir com ela faria "não achei"
 * parecer que o cadastro não existe.
 */
export function SeletorDeEmpresa(props: {
  readonly escolhida: EmpresaResumo | null;
  readonly aoEscolher: (empresa: EmpresaResumo | null) => void;
  readonly ajuda?: string;
  /** Tira o asterisco: o formulário segue sem empresa. */
  readonly opcional?: boolean;
}): JSX.Element {
  const [termo, setTermo] = useState('');
  const [termoAplicado, setTermoAplicado] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setTermoAplicado(termo.trim()), 300);
    return () => clearTimeout(t);
  }, [termo]);

  const busca = useQuery({
    queryKey: ['empresas', 'seletor-de-empresa', termoAplicado],
    queryFn: () => listarEmpresas({ busca: termoAplicado, porPagina: RESULTADOS }),
    enabled: props.escolhida === null && termoAplicado.length >= 2,
  });

  if (props.escolhida !== null) {
    const e = props.escolhida;
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-conteudo-secundario">Empresa</span>
        <div className="flex items-center justify-between gap-3 rounded-md border border-borda px-3 py-2">
          <span className="flex flex-col">
            <span className="text-sm text-conteudo-primario">{e.legalName}</span>
            <span className="valor-fiscal text-xs text-conteudo-sutil">
              {formatarCnpj(e.taxpayerId)}
            </span>
          </span>
          <Botao
            variante="sutil"
            tamanho="pequeno"
            onClick={() => {
              setTermo('');
              props.aoEscolher(null);
            }}
          >
            Trocar
          </Botao>
        </div>
      </div>
    );
  }

  const itens = busca.data?.itens ?? [];
  const total = busca.data?.total ?? 0;

  return (
    <div className="flex flex-col gap-2">
      <Campo
        rotulo="Empresa"
        obrigatorio={props.opcional !== true}
        type="search"
        autoComplete="off"
        placeholder="Razão social ou nome fantasia"
        ajuda={props.ajuda ?? 'Digite ao menos duas letras para buscar.'}
        value={termo}
        onChange={(e) => setTermo(e.target.value)}
        // O seletor mora dentro de formulários: Enter aqui é "buscar", não
        // "enviar o cadastro" com o resto dos campos ainda em branco.
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
      />

      {termoAplicado.length >= 2 && (
        <div aria-live="polite">
          {busca.isPending ? (
            <p className="text-xs text-conteudo-sutil">Buscando…</p>
          ) : busca.isError ? (
            <p className="text-xs text-erro">Não foi possível buscar as empresas. Tente de novo.</p>
          ) : itens.length === 0 ? (
            <p className="text-xs text-conteudo-sutil">
              Nenhuma empresa corresponde a &quot;{termoAplicado}&quot;. A busca cobre razão social
              e nome fantasia.
            </p>
          ) : (
            <ul className="flex flex-col gap-1" aria-label="Empresas encontradas">
              {itens.map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    disabled={!e.isActive}
                    onClick={() => props.aoEscolher(e)}
                    className="flex w-full items-center justify-between gap-3 rounded-md border border-borda px-3 py-2 text-left transition-colors hover:border-acento-ciano focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-borda"
                  >
                    <span className="flex flex-col">
                      <span className="text-sm text-conteudo-primario">{e.legalName}</span>
                      <span className="valor-fiscal text-xs text-conteudo-sutil">
                        {formatarCnpj(e.taxpayerId)}
                      </span>
                    </span>
                    {!e.isActive && <span className="text-xs text-conteudo-sutil">Inativa</span>}
                  </button>
                </li>
              ))}
              {total > itens.length && (
                <li className="px-1 text-xs text-conteudo-sutil">
                  Mostrando {itens.length} de {total}. Refine a busca para achar as demais.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
